import { KEYS, type HitboxPoint, type KeyDef } from './keymap';

const CALIBRATE = typeof location !== 'undefined'
  && new URLSearchParams(location.search).has('calibrate');
const STORAGE_KEY = 'cybiko.hitboxCalibration';
const MIN_RECT_SIZE = 0.004;
const SVG_NS = 'http://www.w3.org/2000/svg';

interface PercentPoint {
  readonly x: number;
  readonly y: number;
}

interface PercentRect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

interface CalibrationShape {
  readonly rect: PercentRect;
  readonly shape?: HitboxShape;
  readonly polygon?: readonly PercentPoint[];
}

type HitboxShape = NonNullable<KeyDef['shape']>;
type CalibrationMode = 'rect' | 'circle' | 'polygon';
type CalibrationBoxKind = 'rect' | 'circle';

const CALIBRATABLE_KEYS = KEYS.filter((key) => key.renderHitbox !== false);

let startPoint: PercentPoint | undefined;
let readout: HTMLOutputElement | undefined;
let dragBox: HTMLElement | undefined;
let shapeLayer: SVGSVGElement | undefined;
let keySelect: HTMLSelectElement | undefined;
let modeSelect: HTMLSelectElement | undefined;
let hitboxPreviewButton: HTMLButtonElement | undefined;
let exportArea: HTMLTextAreaElement | undefined;
let activeKeyId = CALIBRATABLE_KEYS[0]?.id ?? '';
let mode: CalibrationMode = 'rect';
let shapes = new Map<string, CalibrationShape>();

export function initCalibration(frame: HTMLElement): void {
  if (!CALIBRATE) return;

  document.body.classList.add('calibration-mode');
  shapes = loadShapes();
  createPanel();

  shapeLayer = document.createElementNS(SVG_NS, 'svg');
  shapeLayer.classList.add('calibration-shape-layer');
  shapeLayer.setAttribute('viewBox', '0 0 100 100');
  shapeLayer.setAttribute('preserveAspectRatio', 'none');
  frame.appendChild(shapeLayer);

  dragBox = document.createElement('div');
  dragBox.className = 'calibration-box';
  frame.appendChild(dragBox);

  syncHitboxes();
  setActiveKey(activeKeyId);
  updateExport();
  updateReadout(`selected ${activeKeyId}`);

  frame.addEventListener('pointerdown', (event) => {
    stopCalibrationEvent(event);
    const point = eventToPercent(frame, event);

    if (mode === 'polygon') {
      addPolygonPoint(activeKeyId, point);
      return;
    }

    startPoint = point;
    drawRect(rectFromDrag(startPoint, startPoint), mode === 'circle' ? 'circle' : 'rect');
    updateReadout(`point ${formatFramePoint(startPoint)}`);
    try {
      frame.setPointerCapture(event.pointerId);
    } catch {
      // The calibration gesture still works if the browser declines capture.
    }
  }, { capture: true });

  frame.addEventListener('pointermove', (event) => {
    if (!startPoint || !isDragMode(mode)) return;
    stopCalibrationEvent(event);
    drawRect(rectFromDrag(startPoint, eventToPercent(frame, event)), mode === 'circle' ? 'circle' : 'rect');
  }, { capture: true });

  frame.addEventListener('pointerup', (event) => {
    if (!startPoint || !isDragMode(mode)) return;
    stopCalibrationEvent(event);
    const endPoint = eventToPercent(frame, event);
    const rect = rectFromDrag(startPoint, endPoint);
    if (rect.width >= MIN_RECT_SIZE && rect.height >= MIN_RECT_SIZE) {
      if (mode === 'circle') {
        commitCircle(activeKeyId, rect);
        updateReadout(`${activeKeyId} circle ${formatRect(rect)}`);
        console.info(`[calibrate] ${activeKeyId} circle ${formatRect(rect)}`);
      } else {
        commitRect(activeKeyId, rect);
        updateReadout(`${activeKeyId} rect ${formatRect(rect)}`);
        console.info(`[calibrate] ${activeKeyId} rect ${formatRect(rect)}`);
      }
    } else {
      updateReadout(`point ${formatFramePoint(endPoint)}`);
      console.info(`[calibrate] point ${formatFramePoint(endPoint)}`);
    }
    startPoint = undefined;
  }, { capture: true });

  frame.addEventListener('pointercancel', (event) => {
    stopCalibrationEvent(event);
    startPoint = undefined;
    drawRect(undefined);
  }, { capture: true });
}

function createPanel(): void {
  const panel = document.createElement('section');
  panel.className = 'calibration-panel';
  panel.setAttribute('aria-label', 'Hitbox calibration');

  const selectLabel = document.createElement('label');
  selectLabel.className = 'calibration-label';
  selectLabel.textContent = 'key';

  keySelect = document.createElement('select');
  keySelect.className = 'calibration-select';
  keySelect.setAttribute('aria-label', 'Hitbox key');
  for (const key of CALIBRATABLE_KEYS) {
    const option = document.createElement('option');
    option.value = key.id;
    option.textContent = key.id;
    keySelect.appendChild(option);
  }
  keySelect.value = activeKeyId;
  keySelect.addEventListener('change', () => {
    setActiveKey(keySelect?.value ?? activeKeyId);
  });
  selectLabel.appendChild(keySelect);

  const modeLabel = document.createElement('label');
  modeLabel.className = 'calibration-label';
  modeLabel.textContent = 'mode';

  modeSelect = document.createElement('select');
  modeSelect.className = 'calibration-mode-select';
  modeSelect.setAttribute('aria-label', 'Calibration mode');
  for (const value of ['rect', 'circle', 'polygon'] as const) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = value;
    modeSelect.appendChild(option);
  }
  modeSelect.value = mode;
  modeSelect.addEventListener('change', () => {
    mode = parseMode(modeSelect?.value);
    updateReadout(`${activeKeyId} ${mode}`);
  });
  modeLabel.appendChild(modeSelect);

  const buttonRow = document.createElement('div');
  buttonRow.className = 'calibration-actions';

  const copyButton = makeActionButton('copy', () => {
    void copyExport();
  });
  hitboxPreviewButton = makeActionButton('show keys', toggleHitboxPreview);
  const undoButton = makeActionButton('undo', undoPolygonPoint);
  const clearButton = makeActionButton('clear', clearPolygon);
  const resetButton = makeActionButton('reset', resetShapes);

  buttonRow.append(copyButton, hitboxPreviewButton, undoButton, clearButton, resetButton);

  readout = document.createElement('output');
  readout.className = 'calibration-readout';
  readout.value = 'calibrate';

  exportArea = document.createElement('textarea');
  exportArea.className = 'calibration-export';
  exportArea.readOnly = true;
  exportArea.spellcheck = false;
  exportArea.rows = 9;
  exportArea.setAttribute('aria-label', 'Hitbox coordinate export');

  panel.append(selectLabel, modeLabel, buttonRow, readout, exportArea);
  document.body.appendChild(panel);
}

function makeActionButton(label: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  button.addEventListener('click', onClick);
  return button;
}

function stopCalibrationEvent(event: PointerEvent): void {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

function setActiveKey(keyId: string): void {
  if (!CALIBRATABLE_KEYS.some((key) => key.id === keyId)) return;
  activeKeyId = keyId;
  if (keySelect) keySelect.value = keyId;
  for (const hitbox of document.querySelectorAll<HTMLElement>('.hitbox')) {
    hitbox.classList.toggle('is-calibrating', hitbox.dataset.key === keyId);
  }
  const shape = getShape(keyId);
  drawRect(shape.rect, shape.shape === 'round' && !shape.polygon ? 'circle' : 'rect');
  drawPolygon(shape);
  updateReadout(`selected ${activeKeyId}`);
}

function toggleHitboxPreview(): void {
  const show = document.body.classList.toggle('show-calibration-hitboxes');
  if (hitboxPreviewButton) {
    hitboxPreviewButton.textContent = show ? 'hide keys' : 'show keys';
  }
}

function commitRect(keyId: string, rect: PercentRect): void {
  const current = getShape(keyId);
  const next: CalibrationShape = {
    rect,
    shape: current.shape,
    polygon: current.polygon,
  };
  shapes.set(keyId, next);
  applyHitboxShape(keyId, next);
  saveShapes();
  updateExport();
  drawRect(rect);
  drawPolygon(next);
}

function commitCircle(keyId: string, rect: PercentRect): void {
  const next: CalibrationShape = { rect, shape: 'round' };
  shapes.set(keyId, next);
  applyHitboxShape(keyId, next);
  saveShapes();
  updateExport();
  drawRect(rect, 'circle');
  drawPolygon(next);
}

function addPolygonPoint(keyId: string, framePoint: PercentPoint): void {
  const current = getShape(keyId);
  const polygon = [...shapeToFramePolygon(current), framePoint];
  const next = shapeFromFramePolygon(polygon, current.rect, current.shape);
  shapes.set(keyId, next);
  applyHitboxShape(keyId, next);
  saveShapes();
  updateExport();
  drawRect(next.rect);
  drawPolygon(next);
  updateReadout(`${keyId} polygon ${polygon.length} ${formatFramePoint(framePoint)}`);
  console.info(`[calibrate] ${keyId} polygon ${polygon.length} ${formatFramePoint(framePoint)}`);
}

function undoPolygonPoint(): void {
  const current = getShape(activeKeyId);
  if (!current.polygon || current.polygon.length === 0) return;
  const polygon = shapeToFramePolygon(current).slice(0, -1);
  const next = polygon.length > 0
    ? shapeFromFramePolygon(polygon, current.rect, current.shape)
    : { rect: current.rect, shape: current.shape };
  shapes.set(activeKeyId, next);
  applyHitboxShape(activeKeyId, next);
  saveShapes();
  updateExport();
  drawRect(next.rect);
  drawPolygon(next);
  updateReadout(`${activeKeyId} polygon ${polygon.length}`);
}

function clearPolygon(): void {
  const current = getShape(activeKeyId);
  const next: CalibrationShape = { rect: current.rect, shape: current.shape };
  shapes.set(activeKeyId, next);
  applyHitboxShape(activeKeyId, next);
  saveShapes();
  updateExport();
  drawPolygon(next);
  updateReadout(`${activeKeyId} polygon cleared`);
}

function syncHitboxes(): void {
  for (const [keyId, shape] of shapes) {
    applyHitboxShape(keyId, shape);
  }
}

function applyHitboxShape(keyId: string, shape: CalibrationShape): void {
  const hitbox = findHitbox(keyId);
  if (!hitbox) return;
  hitbox.style.top = pct(shape.rect.top);
  hitbox.style.left = pct(shape.rect.left);
  hitbox.style.width = pct(shape.rect.width);
  hitbox.style.height = pct(shape.rect.height);
  hitbox.classList.remove('hitbox--round', 'hitbox--pill');
  hitbox.classList.add(`hitbox--${shape.shape ?? 'round'}`);
  hitbox.style.clipPath = shape.polygon && shape.polygon.length >= 3
    ? polygonToCss(shape.polygon)
    : '';
}

function findHitbox(keyId: string): HTMLElement | undefined {
  return Array.from(document.querySelectorAll<HTMLElement>('.hitbox'))
    .find((hitbox) => hitbox.dataset.key === keyId);
}

function eventToPercent(frame: HTMLElement, event: PointerEvent): PercentPoint {
  const box = frame.getBoundingClientRect();
  return {
    x: clamp((event.clientX - box.left) / box.width),
    y: clamp((event.clientY - box.top) / box.height),
  };
}

function framePointToLocal(rect: PercentRect, point: PercentPoint): PercentPoint {
  return {
    x: rect.width > 0 ? clamp((point.x - rect.left) / rect.width) : 0,
    y: rect.height > 0 ? clamp((point.y - rect.top) / rect.height) : 0,
  };
}

function rectFromPoints(a: PercentPoint, b: PercentPoint): PercentRect {
  const left = Math.min(a.x, b.x);
  const top = Math.min(a.y, b.y);
  return {
    top,
    left,
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
}

function circleFromPoints(a: PercentPoint, b: PercentPoint): PercentRect {
  const xSign = b.x < a.x ? -1 : 1;
  const ySign = b.y < a.y ? -1 : 1;
  const xSpace = xSign > 0 ? 1 - a.x : a.x;
  const ySpace = ySign > 0 ? 1 - a.y : a.y;
  const size = Math.min(Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y)), xSpace, ySpace);
  return {
    top: ySign > 0 ? a.y : a.y - size,
    left: xSign > 0 ? a.x : a.x - size,
    width: size,
    height: size,
  };
}

function rectFromDrag(a: PercentPoint, b: PercentPoint): PercentRect {
  return mode === 'circle' ? circleFromPoints(a, b) : rectFromPoints(a, b);
}

function drawRect(rect: PercentRect | undefined, kind: CalibrationBoxKind = 'rect'): void {
  if (!dragBox) return;
  if (!rect) {
    dragBox.hidden = true;
    return;
  }
  dragBox.hidden = false;
  dragBox.classList.toggle('calibration-box--circle', kind === 'circle');
  dragBox.style.top = pct(rect.top);
  dragBox.style.left = pct(rect.left);
  dragBox.style.width = pct(rect.width);
  dragBox.style.height = pct(rect.height);
}

function drawPolygon(shape: CalibrationShape): void {
  if (!shapeLayer) return;
  shapeLayer.replaceChildren();
  const polygon = shapeToFramePolygon(shape);
  if (polygon.length === 0) return;

  const pointString = polygon.map((point) => {
    return `${fixed(point.x * 100)},${fixed(point.y * 100)}`;
  }).join(' ');

  const outline = document.createElementNS(
    SVG_NS,
    polygon.length >= 3 ? 'polygon' : 'polyline',
  );
  outline.setAttribute('points', pointString);
  outline.classList.add('calibration-polygon');
  shapeLayer.appendChild(outline);

  for (const point of polygon) {
    const marker = document.createElementNS(SVG_NS, 'circle');
    marker.setAttribute('cx', fixed(point.x * 100));
    marker.setAttribute('cy', fixed(point.y * 100));
    marker.setAttribute('r', '0.75');
    marker.classList.add('calibration-polygon-point');
    shapeLayer.appendChild(marker);
  }
}

function shapeToFramePolygon(shape: CalibrationShape): PercentPoint[] {
  return (shape.polygon ?? []).map((point) => localPointToFrame(shape.rect, point));
}

function shapeFromFramePolygon(
  polygon: readonly PercentPoint[],
  fallbackRect: PercentRect,
  shape: HitboxShape | undefined,
): CalibrationShape {
  const rect = rectAroundFramePoints(polygon, fallbackRect);
  return {
    rect,
    shape,
    polygon: polygon.map((point) => framePointToLocal(rect, point)),
  };
}

function rectAroundFramePoints(
  points: readonly PercentPoint[],
  fallbackRect: PercentRect,
): PercentRect {
  if (points.length === 0) return fallbackRect;

  let left = Math.min(...points.map((point) => point.x));
  let right = Math.max(...points.map((point) => point.x));
  let top = Math.min(...points.map((point) => point.y));
  let bottom = Math.max(...points.map((point) => point.y));

  if (right - left < MIN_RECT_SIZE) {
    const center = (left + right) / 2;
    left = clampStart(center - MIN_RECT_SIZE / 2, MIN_RECT_SIZE);
    right = left + MIN_RECT_SIZE;
  }

  if (bottom - top < MIN_RECT_SIZE) {
    const center = (top + bottom) / 2;
    top = clampStart(center - MIN_RECT_SIZE / 2, MIN_RECT_SIZE);
    bottom = top + MIN_RECT_SIZE;
  }

  return {
    top,
    left,
    width: right - left,
    height: bottom - top,
  };
}

function clampStart(value: number, size: number): number {
  return Math.min(1 - size, Math.max(0, value));
}

function localPointToFrame(rect: PercentRect, point: PercentPoint): PercentPoint {
  return {
    x: rect.left + point.x * rect.width,
    y: rect.top + point.y * rect.height,
  };
}

function updateExport(): void {
  if (!exportArea) return;
  exportArea.value = `{\n${CALIBRATABLE_KEYS.map((key) => {
    const shape = getShape(key.id);
    return `  "${key.id}": ${formatShape(shape, key.shape)}`;
  }).join(',\n')}\n}`;
}

function formatShape(shape: CalibrationShape, defaultShape: KeyDef['shape']): string {
  const shapeKind = shape.shape && shape.shape !== defaultShape
    ? `,\n    "shape": "${shape.shape}"`
    : '';
  const polygon = shape.polygon && shape.polygon.length >= 3
    ? `,\n    "polygon": ${formatCssPolygon(shape.polygon)}`
    : '';
  return `{\n    "position": ${formatCssRect(shape.rect)}${shapeKind}${polygon}\n  }`;
}

async function copyExport(): Promise<void> {
  if (!exportArea) return;
  try {
    await navigator.clipboard.writeText(exportArea.value);
    updateReadout('copied');
  } catch {
    exportArea.select();
    updateReadout('copy manually');
  }
}

function resetShapes(): void {
  shapes = loadDefaultShapes();
  saveShapes();
  syncHitboxes();
  setActiveKey(activeKeyId);
  updateExport();
  updateReadout('reset');
}

function updateReadout(value: string): void {
  if (!readout) return;
  readout.value = value;
  readout.textContent = value;
}

function getShape(keyId: string): CalibrationShape {
  const existing = shapes.get(keyId);
  if (existing) return existing;
  const key = KEYS.find((item) => item.id === keyId) ?? KEYS[0];
  if (!key) return { rect: { top: 0, left: 0, width: 0, height: 0 } };
  return keyToShape(key);
}

function loadShapes(): Map<string, CalibrationShape> {
  const defaults = loadDefaultShapes();
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return defaults;
  }
  if (!raw) return defaults;

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) return defaults;
    for (const key of KEYS) {
      const value = parsed[key.id];
      const defaultShape = defaults.get(key.id)?.shape;
      if (isShape(value)) {
        defaults.set(key.id, normalizeShape(value, defaultShape));
      } else if (isRect(value)) {
        // Backward-compatible with earlier rect-only calibration drafts.
        defaults.set(key.id, { rect: value, shape: defaultShape });
      }
    }
  } catch {
    return defaults;
  }
  return defaults;
}

function loadDefaultShapes(): Map<string, CalibrationShape> {
  return new Map(KEYS.map((key) => [key.id, keyToShape(key)]));
}

function keyToShape(key: (typeof KEYS)[number]): CalibrationShape {
  return {
    rect: positionToRect(key.position),
    shape: key.shape,
    polygon: key.polygon ? key.polygon.map(cssPointToNumber) : undefined,
  };
}

function normalizeShape(shape: CalibrationShape, defaultShape: HitboxShape | undefined): CalibrationShape {
  return {
    rect: shape.rect,
    shape: shape.shape ?? defaultShape,
    polygon: shape.polygon && shape.polygon.length > 0
      ? shape.polygon.map((point) => ({ x: clamp(point.x), y: clamp(point.y) }))
      : undefined,
  };
}

function saveShapes(): void {
  const value: Record<string, CalibrationShape> = {};
  for (const key of KEYS) {
    value[key.id] = getShape(key.id);
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Export text remains available if storage is unavailable.
  }
}

function positionToRect(position: {
  readonly top: string;
  readonly left: string;
  readonly width: string;
  readonly height: string;
}): PercentRect {
  return {
    top: cssPercentToNumber(position.top),
    left: cssPercentToNumber(position.left),
    width: cssPercentToNumber(position.width),
    height: cssPercentToNumber(position.height),
  };
}

function cssPointToNumber(point: HitboxPoint): PercentPoint {
  return {
    x: cssPercentToNumber(point.x),
    y: cssPercentToNumber(point.y),
  };
}

function cssPercentToNumber(value: string): number {
  return clamp(parseFloat(value) / 100);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isShape(value: unknown): value is CalibrationShape {
  if (!isRecord(value)) return false;
  const maybePolygon = value.polygon;
  const maybeShape = value.shape;
  return isRect(value.rect)
    && (maybeShape === undefined || isHitboxShape(maybeShape))
    && (maybePolygon === undefined
      || (Array.isArray(maybePolygon) && maybePolygon.every(isPoint)));
}

function isRect(value: unknown): value is PercentRect {
  if (!isRecord(value)) return false;
  return isFiniteUnit(value.top)
    && isFiniteUnit(value.left)
    && isFiniteUnit(value.width)
    && isFiniteUnit(value.height);
}

function isPoint(value: unknown): value is PercentPoint {
  if (!isRecord(value)) return false;
  return isFiniteUnit(value.x) && isFiniteUnit(value.y);
}

function isFiniteUnit(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
}

function isHitboxShape(value: unknown): value is HitboxShape {
  return value === 'round' || value === 'pill';
}

function parseMode(value: unknown): CalibrationMode {
  return value === 'polygon' || value === 'circle' ? value : 'rect';
}

function isDragMode(value: CalibrationMode): value is Exclude<CalibrationMode, 'polygon'> {
  return value === 'rect' || value === 'circle';
}

function polygonToCss(points: readonly PercentPoint[]): string {
  return `polygon(${points.map((point) => `${pct(point.x)} ${pct(point.y)}`).join(', ')})`;
}

function formatFramePoint(point: PercentPoint): string {
  return `{ "left": ${fixed(point.x)}, "top": ${fixed(point.y)} }`;
}

function formatRect(rect: PercentRect): string {
  return `{ "top": ${fixed(rect.top)}, "left": ${fixed(rect.left)}, "width": ${fixed(rect.width)}, "height": ${fixed(rect.height)} }`;
}

function formatCssRect(rect: PercentRect): string {
  return `{ "top": "${pct(rect.top)}", "left": "${pct(rect.left)}", "width": "${pct(rect.width)}", "height": "${pct(rect.height)}" }`;
}

function formatCssPolygon(points: readonly PercentPoint[]): string {
  return `[${points.map((point) => `{ "x": "${pct(point.x)}", "y": "${pct(point.y)}" }`).join(', ')}]`;
}

function fixed(value: number): string {
  return value.toFixed(4);
}

function pct(value: number): string {
  return `${(value * 100).toFixed(3)}%`;
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value));
}
